<section class="create-audience">
  <header class="audience-header">
    <div>
      <h1>
        {{
          mode === 'edit'
            ? ('audiences.actions.edit' | translate)
            : mode === 'clone'
              ? ('audiences.actions.clone' | translate)
              : ('audiences.create.title' | translate)
        }}
      </h1>
      <p>{{ 'audiences.create.subtitle' | translate }}</p>
    </div>
    <div class="actions">
      <button type="button" class="ghost-btn" (click)="handleCancel()">
        {{ 'audiences.actions.cancel' | translate }}
      </button>
      <button type="button"
              class="primary-btn"
              *ngIf="shouldShowPreviewButton"
              (click)="onPreviewRequest()"
              [disabled]="submitting || previewing">
        {{ primaryPreviewButtonLabelKey | translate }}
      </button>
      <button type="button"
              class="primary-btn"
              *ngIf="shouldShowSaveButton && !hasObsoleteFilters"
              (click)="onSubmit()"
              [disabled]="submitting || previewing">
        {{ primarySaveButtonLabelKey | translate }}
      </button>
    </div>
  </header>

  <!-- Owner banner: visible only in Admin mode -->
  <div class="owner-banner" *ngIf="ownerInfo">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
      <circle cx="12" cy="7" r="4"></circle>
    </svg>
    <span class="owner-label">Owner</span>
    <span class="owner-value">{{ ownerInfo.name }}</span>
  </div>

  <div class="create-layout">
    <form class="create-form surface-card" [formGroup]="createForm" (ngSubmit)="onSubmit()">
      <ng-template #similarUsersToggle>
        <div class="similar-users-toggle">
          <span>{{ 'audiences.create.similarUsers.label' | translate }}</span>
          <div class="pill-radio-group pill-radio-group--spaced">
            <label class="pill-option">
              <input type="radio" [value]="true" formControlName="expandSimilarUsers" [attr.disabled]="isSimilarUsersYesDisabled ? true : null">
              <span>{{ 'audiences.create.similarUsers.yes' | translate }}</span>
            </label>
            <label class="pill-option">
              <input type="radio" [value]="false" formControlName="expandSimilarUsers" [attr.disabled]="isSimilarUsersNoDisabled ? true : null">
              <span>{{ 'audiences.create.similarUsers.no' | translate }}</span>
            </label>
          </div>
        </div>
        <ng-container *ngTemplateOutlet="expandVersionToggle"></ng-container>
        <!-- Metodo del lookalike (admin_dashboard.all), solo custom con espansione attiva -->
        <div class="similar-users-toggle" *ngIf="canChooseLookalikeMethod">
          <span>{{ 'audiences.create.lookalikeMethod.label' | translate }}</span>
          <app-custom-select
            formControlName="lookalikeMethod"
            [options]="lookalikeMethodOptions">
          </app-custom-select>
        </div>
      </ng-template>
      <!-- Modalità di espansione (admin_dashboard.all): standard vs pesata IDF.
           Usato sia per le interests (sempre espanse) sia per le custom con espansione attiva. -->
      <ng-template #expandVersionToggle>
        <div class="similar-users-toggle" *ngIf="canUseWeightedExpansion && expandSimilarUsers">
          <span>{{ 'audiences.create.expandVersion.label' | translate }}</span>
          <div class="pill-radio-group pill-radio-group--spaced">
            <label class="pill-option">
              <input type="radio" [value]="false" formControlName="expandVersionWeighted">
              <span>{{ 'audiences.create.expandVersion.standard' | translate }}</span>
            </label>
            <label class="pill-option">
              <input type="radio" [value]="true" formControlName="expandVersionWeighted">
              <span>{{ 'audiences.create.expandVersion.weighted' | translate }}</span>
            </label>
          </div>
          <small class="field-hint" *ngIf="expandVersionCurveUnavailable">
            {{ 'audiences.create.expandVersion.curveUnavailable' | translate }}
          </small>
        </div>
      </ng-template>
      <ng-template #targetAccuracyCard>
        <div class="filters-card surface-card target-accuracy-card" [class.has-error]="isFieldInvalid('targetAccuracy')">
          <div class="target-accuracy-row">
            <div class="section-heading section-heading--accuracy target-accuracy-label">
              <div>
                <h2>{{ 'audiences.create.fields.accuracy' | translate }}</h2>
              </div>
            </div>
            <div class="target-accuracy-content">
              <small class="accuracy-description" *ngIf="!shouldShowPreviewPendingWarning && !shouldShowAccuracyPreview && !interestFiltersChanged">
                {{ 'audiences.create.fields.accuracyDescription' | translate }}
              </small>
              <small class="accuracy-description" *ngIf="!shouldShowPreviewPendingWarning && shouldShowAccuracyPreview">
                {{ 'audiences.create.preview.accuracyDescription' | translate }}
              </small>
              <span class="error-message" *ngIf="isFieldInvalid('targetAccuracy')" style="display: block; margin-bottom: 1rem;">{{ getFieldError('targetAccuracy') }}</span>

              <div class="preview-pending-warning" *ngIf="shouldShowPreviewPendingWarning">
                <strong>{{ 'audiences.create.preview.pendingGraphTitle' | translate }}</strong>
                <p>{{ 'audiences.create.preview.pendingGraphMessage' | translate }}</p>
              </div>

              <!-- Filtri cambiati rispetto all'apertura: la curva salvata non è più valida.
                   Ripristinando i filtri iniziali il grafico riappare identico. -->
              <div class="preview-pending-warning" *ngIf="interestFiltersChanged && !shouldShowPreviewPendingWarning">
                <strong>{{ 'audiences.create.preview.filtersChangedTitle' | translate }}</strong>
                <p>{{ 'audiences.create.preview.filtersChangedMessage' | translate }}</p>
              </div>

              <div class="audience-preview-chart" *ngIf="shouldShowAccuracyPreview && !shouldShowPreviewPendingWarning" [class.is-disabled]="isPreviewInteractionDisabled">
                <div class="audience-preview-chart__main">
                  <div class="audience-preview-chart__chart-col">
                    <div class="audience-preview-chart__svg" (click)="onChartClick($event)">
                      <svg viewBox="0 0 520 260" aria-hidden="true">
                        <defs>
                          <linearGradient id="chartAreaGradient" x1="0" x2="0" y1="0" y2="1">
                            <stop offset="0%" stop-color="#2563eb" stop-opacity="0.06"/>
                            <stop offset="100%" stop-color="#2563eb" stop-opacity="0.01"/>
                          </linearGradient>
                        </defs>
                        <ng-container *ngFor="let tick of previewYAxisTicks">
                          <line [attr.x1]="40" [attr.y1]="tick.y" [attr.x2]="496" [attr.y2]="tick.y" class="audience-preview-chart__grid"></line>
                        </ng-container>
                        <line x1="40" y1="20" x2="40" y2="220" class="audience-preview-chart__axis"></line>
                        <line x1="40" y1="220" x2="496" y2="220" class="audience-preview-chart__axis"></line>
                        <ng-container *ngFor="let tick of previewYAxisTicks">
                          <line x1="36" [attr.y1]="tick.y" x2="40" [attr.y2]="tick.y" class="audience-preview-chart__tick"></line>
                          <text x="30" [attr.y]="tick.y + 4" text-anchor="end" class="audience-preview-chart__label">{{ tick.value }}</text>
                        </ng-container>
                        <line *ngIf="suggestedPreviewPointPosition"
                              [attr.x1]="suggestedPreviewPointPosition.x"
                              y1="20"
                              [attr.x2]="suggestedPreviewPointPosition.x"
                              y2="220"
                              class="audience-preview-chart__suggested-line"></line>
                        <path [attr.d]="previewChartAreaPath" class="audience-preview-chart__area"></path>
                        <path [attr.d]="previewChartPath" class="audience-preview-chart__line"></path>
                        <circle *ngIf="selectedPreviewPointPosition"
                                [attr.cx]="selectedPreviewPointPosition.x"
                                [attr.cy]="selectedPreviewPointPosition.y"
                                r="5"
                                class="audience-preview-chart__point"></circle>
                      </svg>
                    </div>
                    <div class="audience-preview-chart__controls">
                      <div class="audience-preview-chart__slider-row">
                        <button type="button"
                                class="ghost-btn compact audience-preview-chart__arrow"
                                (click)="movePreviewSlider(-1)"
                                [disabled]="isPreviewInteractionDisabled || (getCurrentPreviewStep() ?? previewSliderMin) <= previewSliderMin"
                                aria-label="Previous interval">
                          ‹
                        </button>
                        <input type="range"
                               [min]="previewSliderMin"
                               [max]="previewSliderMax"
                               [value]="getCurrentPreviewStep() ?? previewSliderMin"
                               [disabled]="isPreviewInteractionDisabled"
                               (input)="onPreviewSliderChange($any($event.target).value)"
                               class="audience-preview-chart__slider">
                        <button type="button"
                                class="ghost-btn compact audience-preview-chart__arrow"
                                (click)="movePreviewSlider(1)"
                                [disabled]="isPreviewInteractionDisabled || (getCurrentPreviewStep() ?? previewSliderMin) >= previewSliderMax"
                                aria-label="Next interval">
                          ›
                        </button>
                      </div>
                      <div class="audience-preview-chart__meta" *ngIf="selectedPreviewPoint">
                        <strong>{{ formatNumberWithSeparator(selectedPreviewPoint.users) }} {{ 'audiences.create.preview.users' | translate }}</strong>
                      </div>
                    </div>
                  </div>
                  <div class="audience-preview-chart__sidebar">
                    <button type="button"
                            class="ghost-btn compact audience-preview-chart__reset"
                            (click)="resetPreviewSliderToSuggested()"
                            [disabled]="isPreviewInteractionDisabled || suggestedPreviewPointPosition === null">
                      {{ 'audiences.actions.resetSuggestedPoint' | translate }}
                    </button>
                    <label *ngIf="hasGlobalDataAccess && hasFullRangeData" class="audience-preview-chart__full-range-toggle" (click)="toggleFullCurve()">
                      <span class="toggle-track" [class.is-checked]="showFullCurve"><span class="toggle-thumb"></span></span>
                      <span class="toggle-label">{{ 'audiences.actions.showFullCurve' | translate }}</span>
                    </label>
                  </div>
                </div>
              </div>

              <div class="target-accuracy-options" *ngIf="!shouldShowPreviewPendingWarning && !interestFiltersChanged && (shouldUseLegacyAccuracySelection || !shouldShowAccuracyPreview)">
                <div class="accuracy-pills-wrapper">
                  <label class="pill-option pill-option--accuracy"
                         *ngFor="let level of accuracyLevels">
                    <div class="pill-option__label">
                      <input type="radio" [value]="level" formControlName="audienceQuality">
                      <span>{{ ('audiences.create.accuracy.' + level) | translate }}</span>
                    </div>
                    <span class="pill-option__desc pill-option__desc--inline">
                      {{ ('audiences.create.accuracy.description.' + level) | translate }}
                    </span>
                  </label>
                </div>

                <div class="target-size-field">
                  <input type="text"
                         [value]="getFormattedExtendVolumes()"
                         (input)="onExtendVolumesInput($event)"
                         (change)="onExtendVolumesChange($event)"
                         [placeholder]="'audiences.create.placeholders.audienceSize' | translate">
                  <small class="field-hint">
                    {{ 'audiences.create.fields.audienceSizeHint' | translate }}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ng-template>
      <div class="form-section">
        <div class="section-heading">
<!--          <span class="step-badge">01</span>-->
          <div>
            <h2>{{ 'audiences.create.basic' | translate }}</h2>
          </div>
        </div>
        <div class="form-grid form-grid--basic">
          <div class="form-field type-options">
            <span>
              {{ 'audiences.create.fields.type' | translate }}
              <span aria-hidden="true">*</span>
              <span
                class="info-hint"
                role="img"
                tabindex="0"
                aria-label="{{ 'audiences.create.tooltips.type' | translate }}"
                [attr.data-tooltip]="'audiences.create.tooltips.type' | translate">ℹ</span>
            </span>
            <div class="type-options-row">
              <div class="type-radio-group" role="radiogroup">
                <label class="type-option" *ngFor="let type of audienceTypes">
                  <input
                    type="radio"
                    [value]="type.value"
                    name="audienceType"
                    [disabled]="isAudienceTypeLocked"
                    [checked]="createForm.get('type')?.value === type.value"
                    (change)="onAudienceTypeChange(type.value)">
                  <span>{{ type.labelKey | translate }}</span>
                </label>
                <ng-container *ngIf="hasGlobalDataAccess">
                  <label class="type-option type-option--admin" *ngFor="let type of adminAudienceTypes">
                    <input
                      type="radio"
                      [value]="type.value"
                      name="audienceType"
                      [disabled]="isAudienceTypeLocked"
                      [checked]="createForm.get('type')?.value === type.value"
                      (change)="onAudienceTypeChange(type.value)">
                    <span>{{ type.labelKey | translate }}</span>
                  </label>
                </ng-container>
              </div>
              <label class="is-public-toggle" *ngIf="canMakePublic">
                <input type="checkbox" class="is-public-toggle__input" formControlName="isPublic">
                <span class="toggle-track" [class.is-checked]="createForm.get('isPublic')?.value">
                  <span class="toggle-thumb"></span>
                </span>
                <span class="toggle-label">{{ 'audiences.create.fields.isPublic' | translate }}</span>
              </label>
            </div>
          </div>
          <label class="form-field name-field" [class.has-error]="isFieldInvalid('name')">
            <span>
              {{ 'audiences.create.fields.name' | translate }}
              <span aria-hidden="true">*</span>
            </span>
            <input type="text"
                   formControlName="name"
                   [placeholder]="'audiences.create.placeholders.name' | translate">
            <span class="error-message" *ngIf="isFieldInvalid('name')">{{ getFieldError('name') }}</span>
          </label>
          <label class="form-field description">
            <span>{{ 'audiences.create.fields.description' | translate }}</span>
            <textarea rows="1"
                      formControlName="description"
                      [placeholder]="'audiences.create.placeholders.description' | translate"></textarea>
          </label>
        </div>
      </div>

      <div class="form-section interest-section" *ngIf="isInterestBased">
        <div class="interest-column">
          <div class="section-heading">
<!--            <span class="step-badge">03</span>-->
            <div>
              <h2>{{ 'audiences.create.aiSection' | translate }}</h2>
              <p>{{ 'audiences.create.aiTip' | translate }}</p>
            </div>
          </div>
          <!-- Commentato temporaneamente -->
          <!--
          <div class="ai-description-wrapper">
            <label class="form-field description">
              <span>{{ 'audiences.create.fields.aiPrompt' | translate }}</span>
              <textarea rows="4"
                        formControlName="aiDescription"
                        [placeholder]="'audiences.create.placeholders.aiPrompt' | translate"></textarea>
            </label>
            <div class="analysis-action">
              <button type="button"
                      class="ghost-btn compact"
                      (click)="runAnalysis()"
                      [disabled]="analysisLoading || !createForm.get('aiDescription')?.value">
                <ng-container *ngIf="!analysisLoading; else analysisLoadingTpl">
                  {{ 'audiences.create.actions.analysis' | translate }}
                </ng-container>
              </button>
              <ng-template #analysisLoadingTpl>
                {{ 'audiences.actions.processing' | translate }}
              </ng-template>
            </div>
          </div>
          -->
          <div class="analysis-toolbar">
            <div class="analysis-tags" *ngIf="aiTagsControl?.value?.length">
              <span class="tag-chip"
                    *ngFor="let tag of aiTagsControl.value; let i = index">
                {{ tag }}
                <button type="button" (click)="removeAiTag(i)" aria-label="{{ 'audiences.create.actions.removeTag' | translate }}">
                  ×
                </button>
              </span>
            </div>
            <div class="analysis-add">
              <span class="analysis-add__title">{{ 'audiences.create.actions.addTagTitle' | translate }}</span>
              <div class="analysis-add__controls">
                <input type="text"
                       #tagInput
                       [placeholder]="'audiences.create.placeholders.tag' | translate"
                       (keydown.enter)="addAiTag(tagInput.value); tagInput.value=''; $event.preventDefault();">
                <button type="button" class="ghost-btn compact" (click)="addAiTag(tagInput.value); tagInput.value=''">
                  {{ 'audiences.create.actions.addTag' | translate }}
                </button>
              </div>
              <small class="field-error" *ngIf="aiTagsControl?.touched && aiTagsControl?.errors?.['tagsRequired']">
                {{ 'audiences.create.errors.tagsRequired' | translate }}
              </small>
            </div>
          </div>
          <ng-container *ngIf="isEntityTNCIDPlusMember; else networkOnlyNote">
            <div class="data-source-toggle">
              <span>{{ 'audiences.create.fields.useAllNetworkData' | translate }}</span>
              <div class="pill-radio-group pill-radio-group--spaced">
                <label class="pill-option">
                  <input type="radio" [value]="true" formControlName="useAllNetworkData">
                  <span>{{ 'audiences.create.dataSources.network' | translate }}</span>
                </label>
                <label class="pill-option" [class.pill-option--disabled]="isTNCDataEntity && isInterestBased">
                  <input type="radio" [value]="false" formControlName="useAllNetworkData">
                  <span>{{ 'audiences.create.dataSources.firstParty' | translate }}</span>
                </label>
              </div>
            </div>
          </ng-container>
          <ng-template #networkOnlyNote>
            <p class="data-source-note">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="2" y1="12" x2="22" y2="12"></line>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
              </svg>
              {{ 'audiences.create.dataSources.networkOnly' | translate }}
            </p>
          </ng-template>
          <div class="filters-card surface-card">
            <div class="filters-row">
              <label class="form-field filter-field">
                <span>{{ 'audiences.create.filters.age' | translate }}</span>
                <app-custom-select
                  formControlName="ageRange"
                  [options]="ageOptions"
                  [multiple]="true">
                </app-custom-select>
              </label>
              <label class="form-field filter-field">
                <span>{{ 'audiences.create.filters.gender' | translate }}</span>
                <app-custom-select
                  formControlName="gender"
                  [options]="genderOptions"
                  [multiple]="true">
                </app-custom-select>
              </label>
            </div>
            <div class="location-region-row">
              <label class="form-field location-field">
                <span>{{ 'audiences.create.filters.location' | translate }}</span>
                <div class="location-selector">
                  <div class="location-combobox" (click)="$event.stopPropagation()">
                    <button type="button"
                            class="location-trigger"
                            [class.is-open]="locationDropdownOpen"
                            (click)="toggleLocationDropdown($event)">
                      <span>{{ currentLocationLabel }}</span>
                      <span aria-hidden="true">▾</span>
                    </button>
                    <div class="location-dropdown" *ngIf="locationDropdownOpen">
                      <div class="location-dropdown__search">
                        <input type="text"
                               [value]="locationSearchTerm"
                               (input)="onLocationSearch($event.target.value)"
                               [placeholder]="'audiences.create.placeholders.location' | translate">
                      </div>
                      <div class="location-dropdown__options">
                        <button type="button"
                                *ngFor="let option of filteredCountryOptions"
                                (click)="selectCountry(option, $event)"
                                [class.is-selected]="currentLocationLabel === option.name">
                          {{ option.name }}
                        </button>
                      </div>
                    </div>
                  </div>
                  <button type="button"
                          class="ghost-btn compact location-reset"
                          (click)="setLocationToAll()">
                    {{ 'audiences.create.filters.locationReset' | translate }}
                  </button>
                </div>
              </label>
              <label class="form-field region-field">
                <span>{{ 'audiences.create.fields.region' | translate }}</span>
                <app-custom-select
                  formControlName="regions"
                  [options]="regionOptions"
                  [multiple]="true"
                  [placeholder]="regionSelectionEnabled ? ('audiences.create.fields.regionPlaceholder' | translate) : ('audiences.create.fields.regionDisabled' | translate)">
                </app-custom-select>
              </label>
            </div>
          </div>
        </div>
      </div>
      <!-- Le audience interests sono sempre espanse via embedding: mostra il selettore di modalità (admin) -->
      <div class="form-section" *ngIf="isInterestBased && canUseWeightedExpansion">
        <ng-container *ngTemplateOutlet="expandVersionToggle"></ng-container>
      </div>
      <div class="form-section" *ngIf="isInterestBased && (shouldShowAccuracyPreview || shouldShowPreviewPendingWarning || interestFiltersChanged)">
        <ng-container *ngTemplateOutlet="targetAccuracyCard"></ng-container>
      </div>
      <div class="form-section" *ngIf="isRuleBased">
        <div class="section-heading">
          <div>
            <h2>{{ 'audiences.create.rule.sectionTitle' | translate }}</h2>
            <p>{{ 'audiences.create.rule.sectionSubtitle' | translate }}</p>
          </div>
        </div>
      <div class="rule-config surface-card" formArrayName="ruleBlocks">
            <small class="field-error" *ngIf="ruleBlocks.at(0)?.touched && ruleBlocks.at(0)?.errors?.['ruleFilterRequired']" style="display: block; margin-bottom: 1rem;">
              {{ 'audiences.create.errors.ruleFilterRequired' | translate }}
            </small>
            <div class="rule-block"
                 *ngFor="let block of ruleBlocks.controls; let blockIndex = index; trackBy: trackRuleBlock"
                 [formGroupName]="blockIndex">
              <div class="rule-config-header">
                <div class="rule-block-header">
                  <h3>{{ 'audiences.create.rule.groupsTitle' | translate }} #{{ blockIndex + 1 }}</h3>
                  <button type="button"
                          class="ghost-btn compact"
                          *ngIf="ruleBlocks.length > 1 && blockIndex > 0"
                          (click)="removeRuleBlock(blockIndex)">
                    {{ 'audiences.create.rule.removeBlock' | translate }}
                  </button>
                </div>
                <div class="logic-operator" *ngIf="blockIndex > 0">
                  <span>{{ 'audiences.create.rule.groupOperator' | translate }}</span>
                  <app-custom-select
                    formControlName="operator"
                    [options]="logicOperatorSelectOptions">
                  </app-custom-select>
                </div>
             </div>
              <div class="rule-actions">
                <button
                  *ngFor="let action of ruleActions"
                  type="button"
                  class="ghost-btn compact"
                  (click)="addRuleGroup(blockIndex, action.type)">
                  + {{ action.labelKey | translate }}
                </button>
              </div>
                <div class="rule-groups" formArrayName="groups">
                  <ng-container *ngFor="let group of blockGroups(blockIndex).controls; let groupIndex = index; let last = last; trackBy: trackRuleGroup">
                <div class="rule-group-card surface-card"
                     [formGroupName]="groupIndex">
                  <div class="rule-group-card-header">
                    <strong>{{ ('audiences.create.rule.actions.' + group.get('type')?.value) | translate }}</strong>
                    <button type="button"
                            class="ghost-btn compact"
                            (click)="removeRuleGroup(blockIndex, groupIndex)">
                      {{ 'audiences.create.rule.remove' | translate }}
                    </button>
                  </div>
                  <ng-container [ngSwitch]="group.get('type')?.value">
                    <label class="form-field form-field--notice" *ngSwitchCase="'custom_import'">
                      <span>{{ 'audiences.create.rule.customField' | translate }}</span>
                      <div class="multi-select">
                        <input type="search"
                               class="multi-select-search"
                               [placeholder]="'audiences.create.rule.searchPlaceholder' | translate"
                               #customSearch
                               (input)="0">
                        <ng-container *ngIf="filterOptions(customImportOptions, customSearch.value) as filteredCustomImports">
                          <label class="multi-select-option"
                                 *ngFor="let custom of filteredCustomImports">
                            <input type="checkbox"
                                   [checked]="isPayloadSelected(blockIndex, groupIndex, custom.id)"
                                   (change)="togglePayloadOption(blockIndex, groupIndex, custom.id, $event.target.checked)">
                            <span>{{ custom.name }}</span>
                          </label>
                          <p class="multi-select-empty" *ngIf="!filteredCustomImports.length">
                            {{ 'audiences.create.rule.selectCustomImport' | translate }}
                          </p>
                        </ng-container>
                      </div>
                    </label>
                    <label class="form-field form-field--notice" *ngSwitchCase="'goal'">
                      <span>{{ 'audiences.create.rule.customField' | translate }}</span>
                      <div class="multi-select">
                        <input type="search"
                               class="multi-select-search"
                               [placeholder]="'audiences.create.rule.searchPlaceholder' | translate"
                               #goalSearch
                               (input)="0">
                        <ng-container *ngIf="filterOptions(goalOptions, goalSearch.value) as filteredGoals">
                          <label class="multi-select-option"
                                 *ngFor="let goal of filteredGoals">
                            <input type="checkbox"
                                   [checked]="isPayloadSelected(blockIndex, groupIndex, goal.id)"
                                   (change)="togglePayloadOption(blockIndex, groupIndex, goal.id, $event.target.checked)">
                            <span>{{ goal.name }}</span>
                          </label>
                          <p class="multi-select-empty" *ngIf="!filteredGoals.length">
                            {{ 'audiences.create.rule.selectGoal' | translate }}
                          </p>
                        </ng-container>
                      </div>
                    </label>
                    <label class="form-field form-field--notice" *ngSwitchCase="'audience'">
                      <span>{{ 'audiences.create.rule.customField' | translate }}</span>
                      <div class="multi-select">
                        <input type="search"
                               class="multi-select-search"
                               [placeholder]="'audiences.create.rule.searchPlaceholder' | translate"
                               #audienceSearch
                               (input)="0">
                        <ng-container *ngIf="filterOptions(ruleAudienceOptions, audienceSearch.value) as filteredAudiences">
                          <label class="multi-select-option"
                                 *ngFor="let option of filteredAudiences">
                            <input type="checkbox"
                                   [checked]="isPayloadSelected(blockIndex, groupIndex, option.id)"
                                   (change)="togglePayloadOption(blockIndex, groupIndex, option.id, $event.target.checked)">
                            <span>{{ option.name }}</span>
                          </label>
                          <p class="multi-select-empty" *ngIf="!filteredAudiences.length">
                            {{ 'audiences.create.rule.selectAudience' | translate }}
                          </p>
                        </ng-container>
                      </div>
                    </label>
                    <label class="form-field form-field--notice" *ngSwitchCase="'source'">
                      <span>{{ 'audiences.create.rule.customField' | translate }}</span>
                      <div class="multi-select">
                        <input type="search"
                               class="multi-select-search"
                               [placeholder]="'audiences.create.rule.searchPlaceholder' | translate"
                               #sourceSearch
                               (input)="0">
                        <ng-container *ngIf="filterOptions(sourceGroupOptions, sourceSearch.value) as filteredSources">
                          <label class="multi-select-option"
                                 *ngFor="let source of filteredSources">
                            <input type="checkbox"
                                   [checked]="isPayloadSelected(blockIndex, groupIndex, source.id)"
                                   (change)="togglePayloadOption(blockIndex, groupIndex, source.id, $event.target.checked)">
                            <span>{{ source.name }}</span>
                          </label>
                          <p class="multi-select-empty" *ngIf="!filteredSources.length">
                            {{ 'audiences.create.rule.selectSource' | translate }}
                          </p>
                        </ng-container>
                      </div>
                    </label>
                  </ng-container>
                  <div class="stale-items" *ngIf="getStalePayloadItems(blockIndex, groupIndex).length > 0">
                    <span class="stale-items__label">{{ 'audiences.create.rule.staleItemsWarning' | translate }}</span>
                    <div class="stale-items__tags">
                      <span class="stale-tag" *ngFor="let id of getStalePayloadItems(blockIndex, groupIndex)">
                        <span class="stale-tag__id">{{ id }}</span>
                        <button type="button" class="stale-tag__remove" (click)="removePayloadTag(blockIndex, groupIndex, id)" [attr.aria-label]="'audiences.create.rule.removeStaleItem' | translate">×</button>
                      </span>
                    </div>
                  </div>
                </div>
                <div class="rule-group-operator" *ngIf="!last">
                  <span>AND</span>
                </div>
                </ng-container>
             </div>
            </div>
          </div>
          <div class="rule-block-actions">
            <button type="button" class="ghost-btn" (click)="addRuleBlock()">
              + {{ 'audiences.create.rule.addBlock' | translate }}
            </button>
          </div>
          <div class="rule-preview">
            <p class="rule-preview__title">{{ 'audiences.create.rule.preview.title' | translate }}</p>
            <ng-container *ngIf="rulePreviewBlocks.length; else emptyRulePreview">
              <div class="rule-preview__list">
                <ng-container *ngFor="let block of groupedRulePreviewBlocks; trackBy: trackByPreviewBlock">
                  <div class="rule-preview__operator" *ngIf="block.type === 'operator'">
                    {{ block.value }}
                  </div>
                  <div class="rule-preview__block" *ngIf="block.type === 'block'">
                    <strong>{{ 'audiences.create.rule.preview.block' | translate:{ index: block.index } }}:</strong>
                    <span>{{ block.value }}</span>
                  </div>
                </ng-container>
              </div>
            </ng-container>
          </div>
          <ng-template #emptyRulePreview>
            <p class="rule-preview__empty">
              {{ 'audiences.create.rule.preview.empty' | translate }}
            </p>
          </ng-template>
          <ng-container *ngTemplateOutlet="similarUsersToggle"></ng-container>
          <ng-container *ngIf="expandSimilarUsers && (shouldShowAccuracyPreview || shouldShowPreviewPendingWarning)">
            <ng-container *ngTemplateOutlet="targetAccuracyCard"></ng-container>
          </ng-container>
        </div>

      <div class="form-section" *ngIf="isCustomQueryBased">
        <div class="section-heading">
          <div>
            <h2>{{ 'audiences.create.similarUsers.sectionTitle' | translate }}</h2>
          </div>
        </div>
        <div class="surface-card">
          <ng-container *ngTemplateOutlet="similarUsersToggle"></ng-container>
        </div>
      </div>

<!--      <div class="form-section">-->
<!--        <div class="section-heading">-->
<!--          <span class="step-badge">05</span>-->
<!--          <div>-->
<!--            <h2>{{ 'audiences.create.summary.title' | translate }}</h2>-->
<!--          </div>-->
<!--        </div>-->
<!--        <div class="two-column">-->
<!--          <div class="surface-card summary-card">-->
<!--            <h3>{{ 'audiences.create.aiSection' | translate }}</h3>-->
<!--            <p>{{ 'audiences.create.aiTip' | translate }}</p>-->
<!--          </div>-->
<!--          <div class="surface-card summary-card">-->
<!--            <h3>{{ 'audiences.create.filters.title' | translate }}</h3>-->
<!--            <p>{{ 'audiences.create.subtitle' | translate }}</p>-->
<!--          </div>-->
<!--        </div>-->
<!--      </div>-->

      <div class="form-section" *ngIf="shouldShowScheduleSection">
        <div class="section-heading">
<!--          <span class="step-badge">02</span>-->
          <div>
            <h2>{{ 'audiences.create.scheduleTitle' | translate }}</h2>
          </div>
        </div>
        <div class="date-grid">
          <label class="form-field">
            <span>
              {{ 'audiences.create.fields.frequency' | translate }}
              <span aria-hidden="true">*</span>
            </span>
            <app-custom-select
              formControlName="frequency"
              [options]="frequencyOptions">
            </app-custom-select>
          </label>
          <label class="form-field" [class.has-error]="isFieldInvalid('startDate')">
            <span>
              {{ 'audiences.create.fields.startDate' | translate }}
              <span aria-hidden="true">*</span>
            </span>
            <input type="date" formControlName="startDate">
            <span class="error-message" *ngIf="isFieldInvalid('startDate')">{{ getFieldError('startDate') }}</span>
          </label>
          <label class="form-field">
            <span>
              {{ 'audiences.create.fields.endDate' | translate }}
              <span aria-hidden="true" *ngIf="!createForm.get('ongoing')?.value">*</span>
            </span>
            <input type="date" formControlName="endDate">
          </label>
          <label class="checkbox-field">
            <input type="checkbox" formControlName="ongoing">
            <span>{{ 'audiences.create.fields.ongoing' | translate }}</span>
          </label>
        </div>
      </div>

      <div class="form-actions">
        <button type="button" class="ghost-btn" (click)="handleCancel()">
          {{ 'audiences.actions.cancel' | translate }}
        </button>
        <button type="button"
                class="primary-btn"
                *ngIf="shouldShowPreviewButton"
                (click)="onPreviewRequest()"
                [disabled]="submitting || previewing">
          {{ primaryPreviewButtonLabelKey | translate }}
        </button>
        <button type="submit" class="primary-btn" *ngIf="shouldShowSaveButton && !hasObsoleteFilters" [disabled]="submitting || previewing">
          {{ primarySaveButtonLabelKey | translate }}
        </button>
      </div>
    </form>

    <aside class="create-summary surface-card">
      <h3>{{ 'audiences.create.summary.title' | translate }}</h3>
      <div class="summary-grid">
        <div>
          <label>{{ 'audiences.create.summary.name' | translate }}</label>
          <span>{{ createForm.value.name || '—' }}</span>
          <span class="summary-audience-id" *ngIf="mode === 'edit' && audienceId">
            <span class="summary-audience-id__value" [title]="audienceId">{{ audienceId }}</span>
            <button type="button"
                    class="summary-audience-id__copy"
                    (click)="copyAudienceId()"
                    [class.is-copied]="audienceIdCopied"
                    [attr.aria-label]="'audiences.details.copy' | translate">
              <span aria-hidden="true">
                <svg viewBox="0 0 20 20" focusable="false">
                  <rect x="6" y="2" width="10" height="12" rx="1.5" ry="1.5"></rect>
                  <path d="M4 6h-1.5a1.5 1.5 0 0 0-1.5 1.5v9A1.5 1.5 0 0 0 2.5 18h9a1.5 1.5 0 0 0 1.5-1.5V15"></path>
                </svg>
              </span>
              <span class="sr-only">{{ 'audiences.details.copy' | translate }}</span>
            </button>
          </span>
        </div>
        <div>
          <label>{{ 'audiences.create.summary.audienceType' | translate }}</label>
          <span>
            {{
              isRuleBased
                ? ('audiences.create.types.rule' | translate)
                : ('audiences.create.types.interest' | translate)
            }}
          </span>
        </div>
        <div>
          <label>{{ 'audiences.create.summary.startDate' | translate }}</label>
          <span>{{ createForm.value.startDate || '—' }}</span>
        </div>
        <div>
          <label>{{ 'audiences.create.summary.frequency' | translate }}</label>
          <span>{{ createForm.value.frequency || '—' }}</span>
        </div>
        <div *ngIf="mode === 'edit'">
          <label>{{ 'audiences.create.summary.volumes' | translate }}</label>
          <span *ngIf="actualVolume != null; else volumesPending">{{ actualVolume | number }}</span>
          <ng-template #volumesPending><span>{{ 'audiences.create.summary.notExtracted' | translate }}</span></ng-template>
        </div>
        <div *ngIf="!isCustomQueryBased && (!isRuleBased || expandSimilarUsers) && (shouldShowAccuracyPreview || shouldShowPreviewPendingWarning)">
          <label>{{ 'audiences.create.summary.suggestedVolumes' | translate }}</label>
          <span *ngIf="suggestedPreviewPoint?.users != null; else suggestedPending">{{ suggestedPreviewPoint?.users | number }}</span>
          <ng-template #suggestedPending><span>{{ 'audiences.create.summary.previewPending' | translate }}</span></ng-template>
        </div>
        <ng-container *ngIf="isInterestBased">
          <div>
            <label>{{ 'audiences.create.summary.age' | translate }}</label>
            <span>{{ getAgeSummaryLabel(createForm.value.ageRange) }}</span>
          </div>
          <div>
            <label>{{ 'audiences.create.summary.gender' | translate }}</label>
            <span>{{ getGenderSummaryLabel(createForm.value.gender) }}</span>
          </div>
          <div>
            <label>{{ 'audiences.create.summary.location' | translate }}</label>
            <span>{{ currentLocationLabel }}</span>
          </div>
        </ng-container>
        <div *ngIf="isRuleBased">
          <label>{{ 'audiences.create.summary.conditions' | translate }}</label>
          <ng-container *ngIf="rulePreviewBlocks.length; else summaryEmpty">
            <div class="summary-rule-preview">
              <ng-container *ngFor="let block of groupedRulePreviewBlocks; trackBy: trackByPreviewBlock">
                <div class="summary-rule-operator" *ngIf="block.type === 'operator'">
                  {{ block.value }}
                </div>
                <div class="summary-rule-block" *ngIf="block.type === 'block'">
                  <strong>{{ 'audiences.create.rule.preview.block' | translate:{ index: block.index } }}:</strong>
                  <span>{{ block.value }}</span>
                </div>
              </ng-container>
            </div>
          </ng-container>
          <ng-template #summaryEmpty>
            <span>{{ 'audiences.create.summary.conditionsEmpty' | translate }}</span>
          </ng-template>
        </div>
      </div>
    </aside>
  </div>

  <!-- Conferma per un'audience "interests" senza tag (anteprima o salvataggio). -->
  <div class="modal-backdrop" *ngIf="noInterestsConfirmOpen">
    <div class="modal-card confirmation-modal surface-card" role="dialog" aria-modal="true">
      <header class="modal-header">
        <div>
          <h3>{{ 'audiences.create.noInterestsConfirm.title' | translate }}</h3>
        </div>
        <button type="button"
                class="ghost-btn compact"
                aria-label="{{ 'audiences.create.noInterestsConfirm.cancel' | translate }}"
                (click)="cancelNoInterests()">×</button>
      </header>
      <footer class="modal-actions">
        <button type="button" class="ghost-btn" (click)="cancelNoInterests()">
          {{ 'audiences.create.noInterestsConfirm.cancel' | translate }}
        </button>
        <button type="button" class="primary-btn" (click)="confirmNoInterests()">
          {{ 'audiences.create.noInterestsConfirm.confirm' | translate }}
        </button>
      </footer>
    </div>
  </div>
</section>
